import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Field, FieldLabel } from '../../../../web/components-v2/Field';
import { Button } from '../../../../web/components-v2/Button';
import { Checkbox } from '../../../../web/components-v2/Checkbox';
import { Label } from '../../../../web/components-v2/Label';
import { ISelectOption } from '../../../../web/components/DeprecatedSelect';
import { EditItemPenIcon } from '../../../../web/components/svgs/icons/EditItemPenIcon';
import { LeadRuleViewModel, RecurrenceType, TurnThisOnOptions } from '../../../viewModels/leadrules';
import { styleSheet } from '../styles';
import {
	findDurationOption,
	hourOptions,
	numberRange,
	prepareFromWeeklySchedule,
	prepareToWeeklySchedule,
	turnThisOnOptions,
} from '../utils';
import { CustomDurationPicker } from './CustomDurationPicker';
import { OptionSelect } from './OptionSelect';
import { IScheduleInterval, ScheduleModal } from './ScheduleModal';
import { SetCustomValueModal } from './SetCustomValueModal';

interface IProps {
	leadRule: LeadRuleViewModel;
}

export const generateCustomValueOption = (value: number): ISelectOption<number> => ({
	dataContext: value,
	id: 'custom-value',
	text: String(value),
});

export const getSelectedOption = (leadRulePropValue: number, options: ISelectOption<number>[] = numberRange) => {
	const defaultOption = options.find(item => item.id === 'default');
	const existentOption = options.find(item => item.dataContext === leadRulePropValue);
	const selected = existentOption
		? existentOption
		: leadRulePropValue
			? generateCustomValueOption(leadRulePropValue)
			: defaultOption;
	return selected as ISelectOption<number>;
};

export const RuleContainerHeader: React.FC<IProps> = observer(({ leadRule }) => {
	const defaultTurnThisOn = turnThisOnOptions[0];
	const defaultDuration = hourOptions[0];
	const [selectedTurnThisOn, setTurnThisOn] = useState<ISelectOption<number>>(defaultTurnThisOn);
	const [selectedDuration, setDuration] =
		useState<ISelectOption<{ value: number; unit: 'hours' | 'days' } | number | null>>(defaultDuration);

	const [scheduleModal, setScheduleModal] = useState(false);
	const [schedule, setSchedule] = useState(null);

	const [selectedMaxPerHr, setMaxPerHr] = useState(null);
	const [selectedMaxPerDay, setMaxPerDay] = useState(null);

	const [showCustomPicker, setShowCustomPicker] = useState(false);
	const [customEndDate, setCustomEndDate] = useState<Date | null>(null);

	const secondaryTurnThisOn = turnThisOnOptions[1];

	useEffect(() => {
		if (leadRule?.recurrence === RecurrenceType.AlwaysOn) {
			setTurnThisOn(defaultTurnThisOn);
			setDuration(defaultDuration);
		}
		if (leadRule?.recurrence === RecurrenceType.SingleInterval) {
			// Calculate duration from schedule timestamps
			const ruleSchedule = leadRule?.schedule;
			if (ruleSchedule?.max && ruleSchedule?.min) {
				const durationOption = findDurationOption(ruleSchedule.min, ruleSchedule.max);

				setTurnThisOn(defaultTurnThisOn);
				if (durationOption) {
					setDuration(durationOption);
				} else {
					// No matching preset option, treat as custom
					const endDate = moment(ruleSchedule.max).toDate();
					setCustomEndDate(endDate);
					setDuration(generateCustomDateOption(endDate));
				}
			}
		}
		if (leadRule?.recurrence === RecurrenceType.Weekly) {
			setTurnThisOn(secondaryTurnThisOn);
			setDuration(defaultDuration);
			const prepared = [...(leadRule?.weeklySchedule ?? [])].map(prepareFromWeeklySchedule);

			setSchedule(prepared);
		}

		setMaxPerDay(getSelectedOption(leadRule?.dailyLimit));

		setMaxPerHr(getSelectedOption(leadRule?.hourlyLimit));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [leadRule]);

	useEffect(() => {
		if (!schedule) {
			setTurnThisOn(defaultTurnThisOn);
		} else {
			setTurnThisOn(secondaryTurnThisOn);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [schedule]);

	const handleEnable = () => {
		leadRule?.setEnabled(!leadRule?.isEnabled);
	};

	const handleTurnThisOn = (option: ISelectOption<number>) => {
		setTurnThisOn(option);
		if (option.id === TurnThisOnOptions.Scheduled) {
			setScheduleModal(true);
		} else {
			handleDuration(defaultDuration);
			leadRule?.setSchedule(null);
			leadRule?.setRecurrence(RecurrenceType.AlwaysOn);
		}
	};

	const handleDuration = (option: ISelectOption<{ value: number; unit: 'hours' | 'days' } | number | null>) => {
		if (option.id === RecurrenceType.AlwaysOn) {
			leadRule?.setSchedule(null);
			leadRule?.setRecurrence(RecurrenceType.AlwaysOn);
			setDuration(option);
		} else if (option.id === 'custom') {
			setShowCustomPicker(true);
			// Don't set duration yet, wait for user to pick date
		} else {
			leadRule?.setSchedule(option.dataContext);
			leadRule?.setRecurrence(RecurrenceType.SingleInterval);
			setDuration(option);
		}

		leadRule?.setWeeklySchedule(null);
	};

	const generateCustomDateOption = (date: Date): ISelectOption<null> => ({
		dataContext: null,
		id: 'custom-date',
		text: `Exp ${moment(date).format('M/D, h:mm a')}`,
	});

	const handleCustomDateSave = (endDate: Date) => {
		leadRule?.setScheduleByDate(endDate);
		leadRule?.setRecurrence(RecurrenceType.SingleInterval);
		setCustomEndDate(endDate);
		setDuration(generateCustomDateOption(endDate));
		setShowCustomPicker(false);
	};

	const handleCustomDateCancel = () => {
		setShowCustomPicker(false);
		// If no custom date was previously set, revert to default
		if (!customEndDate) {
			setDuration(defaultDuration);
		}
	};

	const handleMaxPerHr = (option: ISelectOption<number>) => {
		if (option.id !== 'custom') {
			leadRule.setHourlyLimit(option.dataContext);

			setMaxPerHr(option);
			return;
		}

		setMaxPerHr(option);
	};

	const handleMaxPerDay = (option: ISelectOption<number>) => {
		if (option.id !== 'custom') {
			leadRule.setDailyLimit(option.dataContext);

			setMaxPerDay(option);
			return;
		}

		setMaxPerDay(option);
	};

	const handleModalClose = () => {
		setScheduleModal(false);
		if (!schedule) {
			setTurnThisOn(defaultTurnThisOn);
		}
	};

	const handleScheduleSave = (savedSchedule: IScheduleInterval[]) => {
		setSchedule(savedSchedule);
		setScheduleModal(false);
		const prepared = [...savedSchedule].map(prepareToWeeklySchedule);
		leadRule?.setRecurrence(RecurrenceType.Weekly);
		leadRule?.setSchedule(null);

		leadRule?.setWeeklySchedule(prepared);
	};

	const handleEditSchedule = () => setScheduleModal(true);

	const showDuration = selectedTurnThisOn?.id === TurnThisOnOptions.RightAway;

	const isScheduled = selectedTurnThisOn?.id === TurnThisOnOptions.Scheduled;

	return (
		<>
			<div className={css(styleSheet.fieldGrid)}>
				<Field styles={[styleSheet.field, styleSheet.fieldWide]}>
					<FieldLabel htmlFor='blitz-turn-on'>Turn on</FieldLabel>
					<OptionSelect
						id='blitz-turn-on'
						options={turnThisOnOptions}
						selected={selectedTurnThisOn}
						onSelect={handleTurnThisOn}
					/>
				</Field>
				{showDuration && (
					<Field styles={[styleSheet.field, styleSheet.fieldWide]}>
						<FieldLabel htmlFor='blitz-duration'>Duration</FieldLabel>
						<OptionSelect
							id='blitz-duration'
							options={hourOptions}
							selected={selectedDuration}
							onSelect={handleDuration}
						/>
					</Field>
				)}
				<Field styles={[styleSheet.field]}>
					<FieldLabel htmlFor='blitz-max-per-hour'>Max leads/hr</FieldLabel>
					<OptionSelect
						id='blitz-max-per-hour'
						options={numberRange}
						selected={selectedMaxPerHr}
						onSelect={handleMaxPerHr}
					/>
					{selectedMaxPerHr?.id === 'custom' ? (
						<SetCustomValueModal
							onClickToClose={() => setMaxPerHr(getSelectedOption(leadRule?.hourlyLimit))}
							onClickToSave={value => {
								setMaxPerHr(generateCustomValueOption(Number(value)));
								leadRule.setHourlyLimit(Number(value));
							}}
						/>
					) : null}
				</Field>
				<Field styles={[styleSheet.field]}>
					<FieldLabel htmlFor='blitz-max-per-day'>Max leads/day</FieldLabel>
					<OptionSelect
						id='blitz-max-per-day'
						options={numberRange}
						selected={selectedMaxPerDay}
						onSelect={handleMaxPerDay}
					/>
					{selectedMaxPerDay?.id === 'custom' ? (
						<SetCustomValueModal
							onClickToClose={() => setMaxPerDay(getSelectedOption(leadRule?.dailyLimit))}
							onClickToSave={value => {
								setMaxPerDay(generateCustomValueOption(Number(value)));
								leadRule.setDailyLimit(Number(value));
							}}
						/>
					) : null}
				</Field>
			</div>
			<div className={css(styleSheet.actionsRow)}>
				{schedule && isScheduled && (
					<Button variant='link' size='sm' onClick={handleEditSchedule}>
						<EditItemPenIcon fillColor='currentColor' />
						Edit schedule
					</Button>
				)}
				<Label htmlFor='blitz-enabled' styles={[styleSheet.toEnd]}>
					<Checkbox id='blitz-enabled' checked={!!leadRule?.isEnabled} onCheckedChange={handleEnable} />
					Enabled
				</Label>
			</div>
			<ScheduleModal
				values={schedule}
				isOpen={scheduleModal}
				onClickToClose={handleModalClose}
				onClickToSave={handleScheduleSave}
			/>
			<CustomDurationPicker
				initialValue={customEndDate}
				isOpen={showCustomPicker}
				onCancel={handleCustomDateCancel}
				onSave={handleCustomDateSave}
			/>
		</>
	);
});
